import { useRef, useState } from 'react';
import { Icon } from './Icon';
import { Button, IconButton, cx } from './ui';
import { useSpeech } from '../state/useSpeech';

/**
 * Add-a-thing input tuned for a wall-mounted touchscreen.
 *
 * Typing on a wall tablet is the worst part of any of these appliances, so this attacks it
 * three ways, in order of how often each actually gets used:
 *
 *  1. **Don't type.** `suggestions` are one-tap chips of things this list has held before —
 *     a grocery list is the same 40 items forever, so most adds are a single tap.
 *  2. **Talk.** The mic dictates straight into the field where the browser supports it.
 *  3. **Type well.** The field carries the right `inputMode`/`enterKeyHint`/`autoCapitalize`
 *     so the OS raises a sensible keyboard — which is also where swipe/glide typing comes
 *     from on Windows, Android and iPadOS. Nothing here fights the system keyboard.
 */
export function QuickAdd({
  placeholder,
  onAdd,
  suggestions = [],
  busy = false,
}: {
  placeholder: string;
  onAdd: (text: string) => void | Promise<void>;
  suggestions?: string[];
  busy?: boolean;
}) {
  const [text, setText] = useState('');
  const input = useRef<HTMLInputElement>(null);

  const submit = (value: string): void => {
    const t = value.trim();
    if (!t) return;
    void onAdd(t);
    setText('');
    // Keep focus so the OS keyboard stays up for a run of quick adds.
    input.current?.focus();
  };

  const speech = useSpeech((heard) => submit(heard));

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            ref={input}
            value={speech.listening && speech.interim ? speech.interim : text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit(text);
            }}
            placeholder={speech.listening ? 'Listening…' : placeholder}
            // These four attributes are what make the on-screen keyboard behave: the right
            // layout, an "add" action key, sentence casing, and no autocorrect fighting
            // proper nouns.
            inputMode="text"
            enterKeyHint="done"
            autoCapitalize="sentences"
            autoCorrect="off"
            spellCheck={false}
            aria-label={placeholder}
            className={cx(
              'touch-lg w-full rounded-md border bg-surface-high pr-3 pl-3 text-[16px] text-primary',
              'placeholder:text-muted transition-colors focus:outline-none',
              speech.listening ? 'border-accent' : 'border-border focus:border-accent',
            )}
          />
        </div>

        {speech.supported && (
          <IconButton
            icon="mic"
            label={speech.listening ? 'Stop listening' : 'Add by voice'}
            size="lg"
            variant={speech.listening ? 'primary' : 'secondary'}
            onClick={speech.toggle}
            className={speech.listening ? 'animate-pulse' : undefined}
          />
        )}

        <Button
          variant="primary"
          size="lg"
          onClick={() => submit(text)}
          disabled={!text.trim() || busy}
          className="px-5"
        >
          <Icon name="plus" size={16} />
          Add
        </Button>
      </div>

      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          <span className="eyebrow self-center pr-1">Recent</span>
          {suggestions.map((s) => (
            <button
              key={s}
              onClick={() => submit(s)}
              className={cx(
                'rounded-full border border-border-subtle bg-surface px-3 py-1.5 text-[13px] text-secondary',
                'transition-colors hover:border-border hover:bg-surface-high hover:text-primary',
              )}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
